import React from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Stack,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { Appointment, AppointmentStatus } from '../../../types';
import { formatStatusLabel } from './helpers';

interface AppointmentCategoriesProps {
  appointments: Appointment[];
  appointmentsByStatus: Record<string, Appointment[]>;
  weeklyStats: { label: string; count: number }[];
  formatAppointmentDateTime: (appointment: Appointment) => string;
  handleEditClick: (appointment: Appointment) => void;
  handleRescheduleClick: (appointment: Appointment) => void;
}

/** The Categories view: weekly counts, the weekly trend chart, and appointments grouped by status. */
const AppointmentCategories: React.FC<AppointmentCategoriesProps> = ({
  appointments,
  appointmentsByStatus,
  weeklyStats,
  formatAppointmentDateTime,
  handleEditClick,
  handleRescheduleClick,
}) => {
  if (appointments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No appointments yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Weekly Stats
        </Typography>
        <Stack spacing={1}>
          {weeklyStats.map((week) => (
            <Box
              key={week.label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1,
                py: 0.75,
                borderRadius: 1,
                backgroundColor: 'action.hover',
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {week.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {week.count} apt{week.count === 1 ? '' : 's'}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Weekly Trend
          </Typography>
          <Box sx={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyStats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="label" 
                  tick={false}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                />
                <RechartsTooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Paper sx={{ p: 1.5, boxShadow: 2 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {data.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {data.count} appointment{data.count === 1 ? '' : 's'}
                          </Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill="rgba(77, 208, 240, 0.9)" 
                  radius={[4, 4, 0, 0]}
                  maxBarSize={50}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Paper>

      {Object.entries(appointmentsByStatus).map(([status, items]) => (
        <Paper key={status} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {formatStatusLabel(status as AppointmentStatus)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {items.length} appointment{items.length === 1 ? '' : 's'}
            </Typography>
          </Box>
          <Stack spacing={1}>
            {items.map((appointment) => (
              <Paper
                key={appointment.id}
                variant="outlined"
                sx={{
                  p: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => handleEditClick(appointment)}
              >
                <Box>
                  <Typography variant="body2">{appointment.customerName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatAppointmentDateTime(appointment)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="text" onClick={(event) => { event.stopPropagation(); handleEditClick(appointment); }}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRescheduleClick(appointment);
                    }}
                  >
                    Reschedule
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default AppointmentCategories;
